import React, { useCallback, useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { Check, Close, Delete, DoubleArrow, Edit } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import {
  themes,
  tableHeaderSx,
  tableRowSize,
  tableRowSx,
  deleteText,
  deleteMessage,
} from "services/constants";
import {
  customAPI,
  customGetAPI,
  customDeleteAPI,
} from "services/utils/CustomAPI";
import { Loader } from "components/Loader";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { showToast } from "services/utils/Status";

const OTBRAND_HEADERS = [
  { name: "Band Name", key: "band_name", type: "string" },
  { name: "Band Factor", key: "band_factor", type: "number" },
];

const leftAlignedInputSx = {
  "& .MuiInputBase-input": {
    textAlign: "left",
  },
  "& .MuiFormHelperText-root": {
    minHeight: 20,
    marginLeft: 0,
  },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
};

const bodyCellSx = {
  verticalAlign: "top",
  py: 1.5,
};

const displayCellSx = {
  verticalAlign: "middle",
  py: 1.5,
};

const actionCellSx = {
  ...displayCellSx,
  whiteSpace: "nowrap",
};

const actionBoxSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-start",
  flexWrap: "nowrap",
  gap: 1,
  minWidth: 72,
};

const actionIconSx = {
  p: 0.75,
};

export const OTbrands = ({ onClose }) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [otBrands, setOtBrands] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [rowData, setRowData] = useState({ band_name: "", band_factor: "" });
  const [errors, setErrors] = useState({ band_name: "", band_factor: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedband, setSelectedBand] = useState("");

  const endpoint = `${domain}administration/shiftroster/shiftrota/overtime-bands/`;

  const normalizeBandName = useCallback(
    (value) => value?.trim().toLowerCase() ?? "",
    [],
  );

  const isDefaultBand = useCallback(
    (bandName) =>
      ["attendance", "shift earning"].includes(normalizeBandName(bandName)),
    [normalizeBandName],
  );

  const formatBandFactor = useCallback((value) => {
    if (value === null || value === undefined || value === "") return "";
    return String(value);
  }, []);

  const normalizeBands = useCallback(
    (data) =>
      Array.isArray(data)
        ? data.map((item) => ({
            ...item,
            band_factor: formatBandFactor(item.band_factor),
          }))
        : [],
    [formatBandFactor],
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const fetchOtBrands = useCallback(async () => {
    if (!domain) return;
    setLoading(true);
    try {
      const response = await customGetAPI(endpoint);
      if (response.ok) {
        const normalized = normalizeBands(response.responseDetails).filter(
          (item) => !isDefaultBand(item.band_name),
        );
        setOtBrands(normalized);
      }
    } finally {
      setLoading(false);
    }
  }, [domain, endpoint, normalizeBands, isDefaultBand]);

  useEffect(() => {
    fetchOtBrands();
  }, [fetchOtBrands]);

  const validateBandValue = (value) => {
    if (value === "") return "Factor is required";
    const numeric = parseFloat(value);
    if (Number.isNaN(numeric)) return "Enter a numeric value";
    if (numeric < 0 || numeric > 99) return "Value must be between 0 and 99";
    return "";
  };

  const validateRow = () => {
    const trimmedName = rowData.band_name.trim();
    const normalizedName = normalizeBandName(rowData.band_name);
    const hasDuplicateName =
      otBrands.some(
        (band) =>
          normalizeBandName(band.band_name) === normalizedName &&
          band.id !== editingRowId,
      ) || isDefaultBand(trimmedName);
    const nameError =
      trimmedName === ""
        ? "Band name is required"
        : hasDuplicateName
          ? "Band name already exists"
          : "";
    const valueError = validateBandValue(rowData.band_factor);
    setErrors({ band_name: nameError, band_factor: valueError });
    return !nameError && !valueError;
  };

  const createBand = async (payload) => {
    setSaving(true);
    try {
      const response = await customAPI(endpoint, "POST", payload);
      if (response.ok) {
        await fetchOtBrands();
        showToast("success", "Band record created.");
        return true;
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateBand = async (id, payload) => {
    setSaving(true);
    try {
      const response = await customAPI(`${endpoint}${id}/`, "PUT", payload);
      if (response.ok) {
        await fetchOtBrands();
        showToast("success", "Band record updated.");
        return true;
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteBand = async (id) => {
    setSaving(true);
    try {
      const response = await customDeleteAPI(`${endpoint}${id}/`);
      if (response.ok) {
        await fetchOtBrands();
        showToast("success", "Band record deleted.");
        return true;
      }
      return false;
    } finally {
      setSaving(false);
      setShowConfirmationDialog(false);
      setSelectedBand("");
    }
  };

  const handleAddRow = () => {
    setIsAdding(true);
    setEditingRowId(null);
    setRowData({ band_name: "", band_factor: "" });
    setErrors({ band_name: "", band_factor: "" });
  };

  const handleEditRow = (row) => {
    setEditingRowId(row.id);
    setIsAdding(false);
    setRowData({
      band_name: row.band_name,
      band_factor: formatBandFactor(row.band_factor),
    });
    setErrors({ band_name: "", band_factor: "" });
  };

  const handleCancel = () => {
    setEditingRowId(null);
    setIsAdding(false);
    setRowData({ band_name: "", band_factor: "" });
    setErrors({ band_name: "", band_factor: "" });
  };

  const handleSave = async () => {
    if (!validateRow()) return;

    const payload = {
      band_name: rowData.band_name.trim(),
      band_factor: parseFloat(rowData.band_factor),
    };

    if (editingRowId !== null) {
      const success = await updateBand(editingRowId, payload);
      if (!success) return;
      setEditingRowId(null);
    } else {
      const success = await createBand(payload);
      if (!success) return;
      setIsAdding(false);
    }

    setRowData({ band_name: "", band_factor: "" });
    setErrors({ band_name: "", band_factor: "" });
  };

  const handleDelete = async (rowId) => {
    if (!(await deleteBand(rowId))) return;
    if (editingRowId === rowId) {
      handleCancel();
    }
  };

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
  };

  return (
    <Mui.Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <ConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCancelClose}
        onConfirm={() => {
          handleDelete(selectedband?.id);
        }}
        title={deleteText}
        message={deleteMessage(selectedband?.band_name)}
        confirmText={"Delete"}
        cancelText="Cancel"
      />
      <Mui.Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Mui.Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          sx={{ padding: 1, backgroundColor: themes?.primary }}
        >
          <Mui.Box
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: "50%",
            }}
          >
            <Mui.Box sx={{ ml: 1 }}>
              <Mui.IconButton
                onClick={onClose}
                sx={{
                  color: themes?.grayColor,
                  "&:hover": {
                    backgroundColor: themes?.whiteColor,
                  },
                }}
              >
                <DoubleArrow />
              </Mui.IconButton>
            </Mui.Box>
          </Mui.Box>

          <Mui.Box sx={{ ml: 2 }}>
            <Mui.Typography variant="h6" sx={{ fontWeight: 700 }}>
              Overtime Brands
            </Mui.Typography>
            <Mui.Typography variant="body2" color="text.secondary">
              Manage band name and factor. Band factor must be between 0 and 99.
            </Mui.Typography>
          </Mui.Box>
        </Mui.Box>
      </Mui.Paper>

      <Mui.Box sx={{ p: 2, flexGrow: 1, overflow: "auto" }}>
        <Mui.Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            pb: 1,
            borderBottom: `1px solid ${themes?.miliedLightGray}`,
          }}
        >
          <Mui.Typography
            variant="h6"
            sx={{ color: themes?.headLine, fontWeight: 600 }}
          >
            {otBrands.length} Bands
          </Mui.Typography>
          <CustomButton
            actionFuntion={handleAddRow}
            actionName="Add Band"
            typeName="button"
          />
        </Mui.Box>

        {loading ? (
          <Loader />
        ) : (
          <Mui.Box sx={{ mt: 2 }}>
            <Mui.TableContainer
              sx={{
                maxHeight: 695,
                width: "100%",
                border: `1px solid ${themes?.miliedLightGray}`,
                borderRadius: 2,
              }}
            >
              <Mui.Table
                className="border"
                sx={{
                  tableLayout: "fixed",
                }}
              >
                <colgroup>
                  <col style={{ width: "44%" }} />
                  <col style={{ width: "24%" }} />
                  <col style={{ width: "32%" }} />
                </colgroup>
                <Mui.TableHead sx={tableHeaderSx}>
                  <Mui.TableRow sx={tableRowSize}>
                    {OTBRAND_HEADERS.map((header) => (
                      <Mui.TableCell
                        key={header.key}
                        align="left"
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {header.name}
                      </Mui.TableCell>
                    ))}
                    <Mui.TableCell
                      align="left"
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                      }}
                    >
                      &nbsp; &nbsp;Action
                    </Mui.TableCell>
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {isAdding && (
                    <Mui.TableRow sx={{ ...tableRowSx, ...tableRowSize }}>
                      <Mui.TableCell align="left" sx={bodyCellSx}>
                        <Mui.TextField
                          fullWidth
                          size="small"
                          value={rowData.band_name}
                          placeholder="Enter band name"
                          sx={leftAlignedInputSx}
                          onChange={(event) =>
                            setRowData((prev) => ({
                              ...prev,
                              band_name: event.target.value,
                            }))
                          }
                          error={Boolean(errors.band_name)}
                          helperText={errors.band_name || undefined}
                        />
                      </Mui.TableCell>
                      <Mui.TableCell align="left" sx={bodyCellSx}>
                        <Mui.TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter factor"
                          inputProps={{
                            step: "0.01",
                            min: 0,
                            max: 99,
                          }}
                          sx={leftAlignedInputSx}
                          value={rowData.band_factor}
                          onChange={(event) =>
                            setRowData((prev) => ({
                              ...prev,
                              band_factor: event.target.value,
                            }))
                          }
                          error={Boolean(errors.band_factor)}
                          helperText={errors.band_factor || undefined}
                        />
                      </Mui.TableCell>
                      <Mui.TableCell align="left" sx={actionCellSx}>
                        <Mui.Box sx={actionBoxSx}>
                          <Mui.IconButton
                            onClick={handleSave}
                            color="primary"
                            disabled={saving}
                            sx={actionIconSx}
                          >
                            <Check />
                          </Mui.IconButton>
                          <Mui.IconButton
                            onClick={handleCancel}
                            color="error"
                            sx={actionIconSx}
                          >
                            <Close />
                          </Mui.IconButton>
                        </Mui.Box>
                      </Mui.TableCell>
                    </Mui.TableRow>
                  )}

                  {!isAdding && otBrands.length === 0 ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={3} sx={{ py: 3 }}>
                        <TableEmptyState dataLength={otBrands} />
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : (
                    otBrands.map((row) => (
                      <Mui.TableRow
                        key={row.id}
                        sx={{ ...tableRowSx, ...tableRowSize }}
                      >
                        {editingRowId === row.id ? (
                          <>
                            <Mui.TableCell align="left" sx={bodyCellSx}>
                              <Mui.TextField
                                fullWidth
                                size="small"
                                value={rowData.band_name}
                                placeholder="Enter band name"
                                sx={leftAlignedInputSx}
                                onChange={(event) =>
                                  setRowData((prev) => ({
                                    ...prev,
                                    band_name: event.target.value,
                                  }))
                                }
                                error={Boolean(errors.band_name)}
                                helperText={errors.band_name || undefined}
                              />
                            </Mui.TableCell>
                            <Mui.TableCell align="left" sx={bodyCellSx}>
                              <Mui.TextField
                                fullWidth
                                size="small"
                                type="number"
                                placeholder="Enter factor"
                                inputProps={{
                                  step: "0.01",
                                  min: 0,
                                  max: 99,
                                }}
                                sx={leftAlignedInputSx}
                                value={rowData.band_factor}
                                onChange={(event) =>
                                  setRowData((prev) => ({
                                    ...prev,
                                    band_factor: event.target.value,
                                  }))
                                }
                                error={Boolean(errors.band_factor)}
                                helperText={errors.band_factor || undefined}
                              />
                            </Mui.TableCell>
                            <Mui.TableCell align="left" sx={actionCellSx}>
                              <Mui.Box sx={actionBoxSx}>
                                <Mui.IconButton
                                  onClick={handleSave}
                                  color="primary"
                                  disabled={saving}
                                  sx={actionIconSx}
                                >
                                  <Check />
                                </Mui.IconButton>
                                <Mui.IconButton
                                  onClick={handleCancel}
                                  color="error"
                                  sx={actionIconSx}
                                >
                                  <Close />
                                </Mui.IconButton>
                              </Mui.Box>
                            </Mui.TableCell>
                          </>
                        ) : (
                          <>
                            <Mui.TableCell align="left" sx={displayCellSx}>
                              {row.band_name}
                            </Mui.TableCell>
                            <Mui.TableCell align="left" sx={displayCellSx}>
                              {formatBandFactor(row.band_factor)}
                            </Mui.TableCell>
                            <Mui.TableCell align="left" sx={actionCellSx}>
                              <Mui.Box sx={actionBoxSx}>
                                <Mui.IconButton
                                  onClick={() => handleEditRow(row)}
                                  color="primary"
                                  sx={actionIconSx}
                                >
                                  <Edit />
                                </Mui.IconButton>
                                <Mui.IconButton
                                  onClick={() => {
                                    setShowConfirmationDialog(true);
                                    setSelectedBand(row);
                                  }}
                                  color="error"
                                  disabled={isDefaultBand(row.band_name)}
                                  sx={{
                                    ...actionIconSx,
                                    "&.Mui-disabled": {
                                      color: "#98A2B3",
                                    },
                                  }}
                                >
                                  <Delete />
                                </Mui.IconButton>
                              </Mui.Box>
                            </Mui.TableCell>
                          </>
                        )}
                      </Mui.TableRow>
                    ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            {otBrands?.length > 25 && (
              <Mui.TablePagination
                className="custom-pagination"
                component="div"
                rowsPerPageOptions={[25, 50, 75, 100]}
                count={otBrands?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Mui.Box>
        )}
      </Mui.Box>
    </Mui.Box>
  );
};
